import React, { FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Modal, Card, Row, Col, Form, Button, Accordion } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CircularProgress } from '@material-ui/core'
import DatePicker from 'react-datepicker'
import { Formik } from 'formik'
import MaskedInput from 'react-maskedinput'
import FileUploadOneByOne from '../../FileUploadOneByOne'
import BootstrapTable from 'react-bootstrap-table-next'
import ToolkitProvider from 'react-bootstrap-table2-toolkit'
import ActionData from '../../../types/ActionData'
import BackgroundCheckVerificationForm from '../../../types/BackgroundCheckVerificationForm'
import Provider from '../../../types/Provider'
import RootState from '../../../types/RootState'
import { mainColors } from '../../../assets/colors'
import { BackgroundCheckVerificationSchema } from '../../../utils/validation'
import { getDateWithoutTZOffset } from '../../../utils/dataTools'
import moment from 'moment'

import './index.css'

interface Props {
  readonly: boolean;
  show: boolean;
  updatingBackgroundCheckVerification: boolean;
  updateBackgroundCheckVerification: ActionData;
  onClose: () => void;
  onSubmit: (values: any) => void;
}

interface EmployeeProps {
  errors: any;
  index: number;
  readonly: boolean;
  selectedProvider: Provider;
  user: any;
  values: BackgroundCheckVerificationForm;
  handleChange: any;
  setFieldValue: (field: string, value: any) => void;
  setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
}

const EmployeeForm: FC<EmployeeProps> = ({ index, values, errors, readonly, selectedProvider, user, handleChange, setFieldValue, setFieldTouched }: EmployeeProps) => (
  <>
    <Row>
      <Form.Group as={Col} lg={6} sm={12}>
        <Form.Label>Employee Type</Form.Label>
        <Form.Control
          type='text'
          name={`backgroundEmployeeType${index}`}
          value={values[`backgroundEmployeeType${index}`] || ''}
          disabled
        />
      </Form.Group>
    </Row>
    <Row>
      <Form.Group as={Col} lg={6} sm={12}>
        <Form.Label>First Name</Form.Label>
        <Form.Control
          type='text'
          name={`firstName${index}`}
          value={values[`firstName${index}`] || ''}
          disabled
        />
      </Form.Group>
      <Form.Group as={Col} lg={6} sm={12}>
        <Form.Label>Last Name</Form.Label>
        <Form.Control
          type='text'
          name={`lastName${index}`}
          value={values[`lastName${index}`] || ''}
          disabled
        />
      </Form.Group>
    </Row>
    <Row>
      <Form.Group as={Col} lg={6} sm={12}>
        <Form.Label>Date of Hire</Form.Label>
        <DatePicker
          className='disabledDatePicker datepicker'
          dateFormat='MM/dd/yyyy'
          selected={values[`hireDate${index}`] ? getDateWithoutTZOffset(values[`hireDate${index}`]) : null}
          onChange={(date: Date) => setFieldValue(`hireDate${index}`, getDateWithoutTZOffset(date))}
          disabled
        />
      </Form.Group>
    </Row>
    <Row>
      <Form.Group as={Col} lg={6} sm={12}>
        <Form.Label>Date of BID Signed</Form.Label>
        <DatePicker
          className={(errors[`bidDate${index}`]?'invalidDatePicker ': ( readonly ? 'disabledDatePicker ' : '') )+'datepicker'}
          dateFormat='MM/dd/yyyy'
          placeholderText='Type date or click to select date'
          selected={values[`bidDate${index}`] ? getDateWithoutTZOffset(values[`bidDate${index}`]) : null}
          onChange={(date: Date) => setFieldValue(`bidDate${index}`, getDateWithoutTZOffset(date))}
          disabled={readonly}
        />
        {
          errors[`bidDate${index}`] && (
            <Form.Text className='form-error'>
              {errors[`bidDate${index}`]}
            </Form.Text>
          )
        }
      </Form.Group>
      <Form.Group as={Col} lg={6} sm={12}>
        <Form.Label>Date of the DOJ</Form.Label>
        <DatePicker
          className={(errors[`dojDate${index}`]?'invalidDatePicker ': ( readonly ? 'disabledDatePicker ' : '') )+'datepicker'}
          dateFormat='MM/dd/yyyy'
          placeholderText='Type date or click to select date'
          selected={values[`dojDate${index}`] ? getDateWithoutTZOffset(values[`dojDate${index}`]) : null}
          onChange={(date: Date) => setFieldValue(`dojDate${index}`, getDateWithoutTZOffset(date))}
          disabled={readonly}
        />
        {errors[`dojDate${index}`] ? (
          <Form.Text className='form-error'>
            {errors[`dojDate${index}`]}
          </Form.Text>
        ) : null}
      </Form.Group>
    </Row>
    <Row>
      <Form.Group as={Col} xs={12}>
        <Form.Label>DHS Background Check Document</Form.Label>
        <FileUploadOneByOne
          employeeId={values[`backgroundCheckVerificationEmployee${index}`]}
          employeeNumber={index}
          error={errors[`dhsFile${index}`]}
          id='backgroundCheckVerificationEmployeeAttachmentId'
          name={`dhsFile${index}`}
          placeholder='Locate your file(s) to upload'
          readonly={readonly}
          selectedProvider={selectedProvider}
          uploadedFile={values[`uploadedDhsFile${index}`]}
          urlDirectory='pdf/backgroundcheck'
          user={user}
          warningMessage
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
        />
      </Form.Group>
    </Row>
    <Row>
      <Form.Group as={Col} xs={12}>
        <Form.Label>DOJ Background Check Document</Form.Label>
        <FileUploadOneByOne
          employeeId={values[`backgroundCheckVerificationEmployee${index}`]}
          employeeNumber={index}
          error={errors[`dojFile${index}`]}
          id='backgroundCheckVerificationEmployeeAttachmentId'
          name={`dojFile${index}`}
          placeholder='Locate your file(s) to upload'
          readonly={readonly}
          selectedProvider={selectedProvider}
          uploadedFile={values[`uploadedDojFile${index}`]}
          urlDirectory='pdf/backgroundcheck'
          user={user}
          warningMessage
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
        />
      </Form.Group>
    </Row>
    <Row>
      <Form.Group as={Col} xs={12}>
        <Form.Label>Background Information Disclosure (BID)</Form.Label>
        <FileUploadOneByOne
          employeeId={values[`backgroundCheckVerificationEmployee${index}`]}
          employeeNumber={index}
          error={errors[`bidFile${index}`]}
          id='backgroundCheckVerificationEmployeeAttachmentId'
          name={`bidFile${index}`}
          placeholder='Locate your file(s) to upload'
          readonly={readonly}
          selectedProvider={selectedProvider}
          uploadedFile={values[`uploadedBidFile${index}`]}
          urlDirectory='pdf/backgroundcheck'
          user={user}
          warningMessage
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
        />
      </Form.Group>
    </Row>
    <Row>
      <Form.Group as={Col} xs={12}>
        <Form.Label>Comments (Optional)</Form.Label>
        <Form.Control
          name={`comments${index}`}
          value={values[`comments${index}`] || ''}
          as='textarea'
          rows={4}
          onChange={handleChange}
          isInvalid={!!errors[`comments${index}`]}
          disabled={readonly}
        />
        <Form.Control.Feedback type='invalid'>
          {errors[`comments${index}`]}
        </Form.Control.Feedback>
      </Form.Group>
    </Row>
  </>
)

const CardEmployeeForm: FC<EmployeeProps> = (props: EmployeeProps) => (
  <Card style={{ marginTop: 15 }}>
    <Card.Body className='background-check-form-card-body'>
      <Card.Title>{`Employee ${props.index}`}</Card.Title>
      <EmployeeForm
        {...props}
      />
    </Card.Body>
  </Card>
)

const AccordionEmployeeForm: FC<EmployeeProps> = (props: EmployeeProps) => {
  const { index, values } = props

  const accordionProps =  values[`notApplicable${index}`] ? { className: 'background-check-accordion' } : { className: 'background-check-accordion', activeKey: '0' }

  return (
    <Accordion {...accordionProps} >
      <Card>
        <Accordion.Toggle as={Card.Header} eventKey='0' className='background-check-accordion-header' disabled >
          <h5>{`Employee ${index}`}</h5>
          <div className='background-check-na'>
            <span style={{ marginRight: 10 }}>Not Applicable</span>
            <Form.Check
              name={`notApplicable${index}`}
              type='checkbox' 
              label=''
              checked={values[`notApplicable${index}`] || false}
              disabled
              readOnly
            />
          </div>
        </Accordion.Toggle>
        <Accordion.Collapse eventKey='0'>
          <Card.Body className='background-check-form-card-body' style={{ paddingTop: 0 }}>
            {
              !values[`notApplicable${index}`] && (
                <EmployeeForm
                  {...props}
                />
              )
            }
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    </Accordion>
  )
}

const initialBackgroundCheckVerification = {
  backgroundCheckVerificationId: -1,
  providerId: -1,
  organizationName: '',
  providerName: '',
  providerAddress1: '',
  providerAddress2: '',
  providerCity: '',
  providerState: '',
  providerZipCode: '',
  providerPhoneNumber: '',
  status: '',
  backgroundCheckDate: null,
  reviewerFullName: null,
  specialistFullName: null,
  providerPersonnelName: null,
  providerPersonnelPhone: null,
  providerPersonnelExtension: null,
  providerPersonnelEmailAddress: null,
  additionalRecipients: null,
  unlockEmployeeFields: null,
  reviewerNotes: null,
  networkQualityNotes: null,
  backgroundCheckVerificationEmployee1: null,
  backgroundEmployeeType1: null,
  firstName1: null,
  lastName1: null,
  hireDate1: null,
  bidDate1: null,
  dojDate1: null,
  dhsFile1: [],
  dojFile1: [],
  bidFile1: [],
  uploadedDhsFile1: [],
  uploadedDojFile1: [],
  uploadedBidFile1: [],
  comments1: null,
  notApplicable1: null,
  backgroundCheckVerificationEmployee2: null,
  backgroundEmployeeType2: null,
  firstName2: null,
  lastName2: null,
  hireDate2: null,
  bidDate2: null,
  dojDate2: null,
  dhsFile2: [],
  dojFile2: [],
  bidFile2: [],
  uploadedDhsFile2: [],
  uploadedDojFile2: [],
  uploadedBidFile2: [],
  comments2: null,
  notApplicable2: null,
  backgroundCheckVerificationEmployee3: null,
  backgroundEmployeeType3: null,
  firstName3: null,
  lastName3: null,
  hireDate3: null,
  bidDate3: null,
  dojDate3: null,
  dhsFile3: [],
  dojFile3: [],
  bidFile3: [],
  uploadedDhsFile3: [],
  uploadedDojFile3: [],
  uploadedBidFile3: [],
  comments3: null,
  notApplicable3: null,
  backgroundCheckVerificationEmployee4: null,
  backgroundEmployeeType4: null,
  firstName4: null,
  lastName4: null,
  hireDate4: null,
  bidDate4: null,
  dojDate4: null,
  dhsFile4: [],
  dojFile4: [],
  bidFile4: [],
  uploadedDhsFile4: [],
  uploadedDojFile4: [],
  uploadedBidFile4: [],
  comments4: null,
  notApplicable4: null,
  activity: [],
  followUp: []
}

const BackgroundCheckVerificationModal: FC<Props> =
  ({
    readonly,
    show,
    updatingBackgroundCheckVerification,
    updateBackgroundCheckVerification,
    onClose,
    onSubmit,
  }: Props) => {
    const errorText = updateBackgroundCheckVerification.error !== false ? updateBackgroundCheckVerification.error.response.text : ''
    const modalText = updateBackgroundCheckVerification.error !== false ? (errorText.includes('could not be notified') ? '' : 'The data could not be saved') : 'Updating Background Check Verification'
    const loadingIcon = updateBackgroundCheckVerification.error !== false ? (errorText.includes('could not be notified') ? <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} color='orange' className='modal-success-icon' /> : <FontAwesomeIcon icon={['fas', 'exclamation-circle']} color={mainColors.loadingBlue} className='modal-success-icon' />) : <CircularProgress size={70} />

    const [ initialBackgroundCheckVerificationValues, setInitialBackgroundCheckVerificationValues ] = useState(initialBackgroundCheckVerification)

    const { backgroundCheckVerification, selectedProvider, user } = useSelector((state: RootState) => ({
      backgroundCheckVerification: state.backgroundCheckVerification.backgroundCheckVerification.data,
      selectedProvider: state.authentication.selectedProvider,
      user: state.authentication.user,
    }))

    useEffect(() => {
      let employeeData = {}
      if ( backgroundCheckVerification && backgroundCheckVerification.employee && backgroundCheckVerification.employee.length > 0 ) {
        backgroundCheckVerification.employee.forEach((backgroundCheckEmployee, i) => {
          employeeData[`backgroundCheckVerificationEmployee${i+1}`] = backgroundCheckEmployee.backgroundCheckVerificationEmployeeId
          employeeData[`backgroundEmployeeType${i+1}`] = backgroundCheckEmployee.backgroundEmployeeType
          employeeData[`comments${i+1}`] = backgroundCheckEmployee.comments
          employeeData[`firstName${i+1}`] = backgroundCheckEmployee.firstName
          employeeData[`hireDate${i+1}`] = backgroundCheckEmployee.hireDate
          employeeData[`lastName${i+1}`] = backgroundCheckEmployee.lastName
          employeeData[`notApplicable${i+1}`] = backgroundCheckEmployee.notApplicable
          employeeData[`dojDate${i+1}`] = backgroundCheckEmployee.dojDate
          employeeData[`bidDate${i+1}`] = backgroundCheckEmployee.bidDate
          employeeData[`comments${i+1}`] = backgroundCheckEmployee.comments
          employeeData[`dhsFile${i+1}`] = []
          employeeData[`dojFile${i+1}`] = []
          employeeData[`bidFile${i+1}`] = []
          employeeData[`uploadedDhsFile${i+1}`] = []
          employeeData[`uploadedDojFile${i+1}`] = []
          employeeData[`uploadedBidFile${i+1}`] = []
  
          backgroundCheckEmployee.attachment.forEach((attachmentItem) => {
            if ( attachmentItem.attachmentType === 'DHS' ) {
              employeeData[`uploadedDhsFile${i+1}`].push({ backgroundCheckVerificationEmployeeAttachmentId: attachmentItem.backgroundCheckVerificationEmployeeAttachmentId, fileLocation: attachmentItem.fileLocation })
            } else if ( attachmentItem.attachmentType === 'DOJ' ) {
              employeeData[`uploadedDojFile${i+1}`].push({ backgroundCheckVerificationEmployeeAttachmentId: attachmentItem.backgroundCheckVerificationEmployeeAttachmentId, fileLocation: attachmentItem.fileLocation })
            } else if ( attachmentItem.attachmentType === 'BID' ) {
              employeeData[`uploadedBidFile${i+1}`].push({ backgroundCheckVerificationEmployeeAttachmentId: attachmentItem.backgroundCheckVerificationEmployeeAttachmentId, fileLocation: attachmentItem.fileLocation })
            }
          })
        })
      }

      const backgroundCheckData = backgroundCheckVerification ? {
        backgroundCheckVerificationId: backgroundCheckVerification.backgroundCheckVerificationId,
        providerId: backgroundCheckVerification.providerId,
        organizationName: backgroundCheckVerification.organizationName,
        providerName: backgroundCheckVerification.providerName,
        providerAddress1: backgroundCheckVerification.providerAddress1,
        providerAddress2: backgroundCheckVerification.providerAddress2,
        providerCity: backgroundCheckVerification.providerCity,
        providerState: backgroundCheckVerification.providerState,
        providerZipCode: backgroundCheckVerification.providerZipCode,
        providerPhoneNumber: backgroundCheckVerification.providerPhoneNumber,
        status: '',
        backgroundCheckDate: backgroundCheckVerification.backgroundCheckDate,
        reviewerFullName: backgroundCheckVerification.reviewerFullName,
        specialistFullName: backgroundCheckVerification.specialistFullName,
        providerPersonnelName: backgroundCheckVerification.providerPersonnelName,
        providerPersonnelPhone: backgroundCheckVerification.providerPersonnelPhone,
        providerPersonnelExtension: backgroundCheckVerification.providerPersonnelExtension,
        providerPersonnelEmailAddress: backgroundCheckVerification.providerPersonnelEmailAddress,
        additionalRecipients: backgroundCheckVerification.additionalRecipients,
        unlockEmployeeFields: backgroundCheckVerification.unlockEmployeeFields,
        reviewerNotes: backgroundCheckVerification.reviewerNotes,
        networkQualityNotes: backgroundCheckVerification.networkQualityNotes,
        backgroundCheckVerificationEmployee1: null,
        backgroundEmployeeType1: null,
        firstName1: null,
        lastName1: null,
        hireDate1: null,
        bidDate1: null,
        dojDate1: null,
        dhsFile1: [],
        dojFile1: [],
        bidFile1: [],
        uploadedDhsFile1: [],
        uploadedDojFile1: [],
        uploadedBidFile1: [],
        comments1: null,
        notApplicable1: null,
        backgroundCheckVerificationEmployee2: null,
        backgroundEmployeeType2: null,
        firstName2: null,
        lastName2: null,
        hireDate2: null,
        bidDate2: null,
        dojDate2: null,
        dhsFile2: [],
        dojFile2: [],
        bidFile2: [],
        uploadedDhsFile2: [],
        uploadedDojFile2: [],
        uploadedBidFile2: [],
        comments2: null,
        notApplicable2: null,
        backgroundCheckVerificationEmployee3: null,
        backgroundEmployeeType3: null,
        firstName3: null,
        lastName3: null,
        hireDate3: null,
        bidDate3: null,
        dojDate3: null,    
        dhsFile3: [],
        dojFile3: [],
        bidFile3: [],
        uploadedDhsFile3: [],
        uploadedDojFile3: [],
        uploadedBidFile3: [],
        comments3: null,
        notApplicable3: null,
        backgroundCheckVerificationEmployee4: null,
        backgroundEmployeeType4: null,
        firstName4: null,
        lastName4: null,
        hireDate4: null,
        bidDate4: null,
        dojDate4: null,
        dhsFile4: [],
        dojFile4: [],
        bidFile4: [],
        uploadedDhsFile4: [],
        uploadedDojFile4: [],
        uploadedBidFile4: [],
        comments4: null,
        notApplicable4: null,
        activity: backgroundCheckVerification.activity,
        followUp: backgroundCheckVerification.followUp,
      } : initialBackgroundCheckVerification

      setInitialBackgroundCheckVerificationValues( backgroundCheckVerification.employee && backgroundCheckVerification.employee.length > 0 ? { ...backgroundCheckData, ...employeeData } : { ...backgroundCheckData })
    }, [backgroundCheckVerification])

    const columns = [{
      dataField: 'date',
      text: 'Date',
      sort: true,
      headerStyle: { width: 150 },
      style: { width: 150 },
      // eslint-disable-next-line react/display-name
      formatter: (cellContent) => ( cellContent && moment.utc(cellContent).format('MM/DD/YYYY') ) || ''
    }, {
      dataField: 'activityType',
      text: 'Type',
      sort: true,
      headerStyle: { width: 150 },
      style: { width: 150 },
    }, {
      dataField: 'activity',
      text: 'Content',
      sort: true,
    }, {
      dataField: 'username',
      text: 'Author Email',
      sort: true,
    }]

    return (
      <Modal size='xl' show={show} backdrop='static' onHide={onClose}>
        <Modal.Header closeButton id='modal-header'>
          <FontAwesomeIcon icon={['fas', 'edit']} size='lg' className='modal-header-icon' />
          <Modal.Title id='modal-header-title' style={{ width: '100%' }} >
            <div style={{ display: 'flex', flexFlow: 'row', justifyContent: 'space-between', width: '100%' }}>
              <span>Background Check Verification</span>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {
            updatingBackgroundCheckVerification ? (
              updateBackgroundCheckVerification.loading || updateBackgroundCheckVerification.error !== false ? (
                <div className='modal-loading-container'>
                  {loadingIcon}
                  <span className='modal-loading-text' style={updateBackgroundCheckVerification.error !== false ? { width: '100%' } : {}}>{modalText}</span>
                  {
                    updateBackgroundCheckVerification.error !== false ? (<span style={{ width: '100%' }} dangerouslySetInnerHTML={{ __html: errorText }} />) : null
                  }
                </div>
              ) : updateBackgroundCheckVerification.success !== false ? (
                <div>
                  <div className='modal-success-container'>
                    <FontAwesomeIcon icon={['far', 'check-circle']} color={mainColors.successGreen} className='modal-success-icon' />
                    <span className='modal-loading-text'>Success!</span>
                    <span className='modal-success-text'>You have submitted the Background Check Verification for review.</span>
                  </div>
                </div>
              ) : (
                <div></div>
              )
            ) : (
              <Formik
                initialValues={initialBackgroundCheckVerificationValues}
                enableReinitialize
                validationSchema={BackgroundCheckVerificationSchema}
                onSubmit={(values, { setSubmitting }) => {
                  setSubmitting(false)
                  onSubmit(values)
                }}
              >
                {({ errors, values, handleChange, handleSubmit, setFieldValue, setFieldTouched }) => (
                  <Form noValidate onSubmit={handleSubmit}>
                    {console.log(values.dhsFile1)}
                    <Card className='background-check-form-card'>
                      <Card.Body className='background-check-form-card-body'>
                        <Row>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Provider ID</Form.Label>
                            <Form.Control
                              type='text'
                              name='providerName'
                              value={`${values.providerId || ''}`}
                              disabled
                            />
                          </Form.Group>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Date of Background Check Verification</Form.Label>
                            <DatePicker
                              className='disabledDatePicker datepicker'
                              dateFormat='MM/dd/yyyy'
                              selected={values.backgroundCheckDate ? getDateWithoutTZOffset(values.backgroundCheckDate) : null}
                              onChange={() => {}}
                              disabled
                            />
                          </Form.Group>
                        </Row>
                        <Row>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Provider Name</Form.Label>
                            <Form.Control
                              type='text'
                              name='providerName'
                              value={values.providerName || ''}
                              disabled
                            />
                          </Form.Group>
                        </Row>
                        <Row>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Address 1</Form.Label>
                            <Form.Control
                              type='text'
                              name='providerAddress1'
                              value={values.providerAddress1 || ''}
                              disabled
                            />
                          </Form.Group>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Address 2</Form.Label>
                            <Form.Control
                              type='text'
                              name='providerAddress2'
                              value={values.providerAddress2 || ''}
                              disabled
                            />
                          </Form.Group>
                        </Row>
                        <Row>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>City</Form.Label>
                            <Form.Control
                              type='text'
                              name='providerCity'
                              value={values.providerCity || ''}
                              disabled
                            />
                          </Form.Group>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>State</Form.Label>
                            <Form.Control
                              type='text'
                              name='providerState'
                              value={values.providerState || ''}
                              disabled
                            />
                          </Form.Group>
                        </Row>
                        <Row>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Zip</Form.Label>
                            <Form.Control
                              type='text'
                              name='providerZipCode'
                              value={values.providerZipCode || ''}
                              disabled
                            />
                          </Form.Group>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Phone</Form.Label>
                            <Form.Control
                              as={MaskedInput}
                              name='providerPhoneNumber'
                              mask='(111) 111-1111'
                              value={values.providerPhoneNumber || ''}
                              disabled
                            />
                          </Form.Group>
                        </Row>
                        <Row>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>LCI Personnel Conducting Background Check</Form.Label>
                            <Form.Control
                              type='text'
                              name='reviewerFullName'
                              value={values.reviewerFullName || ''}
                              disabled
                            />
                          </Form.Group>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Assigned Provider Specialist</Form.Label>
                            <Form.Control
                              type='text'
                              name='specialistFullName'
                              value={values.specialistFullName || ''}
                              disabled
                            />
                          </Form.Group>
                        </Row>
                        <Row>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Provider Personnel Assisting Background Check</Form.Label>
                            <Form.Control
                              type='text'
                              name='providerPersonnelName'
                              value={values.providerPersonnelName || ''}
                              disabled
                            />
                          </Form.Group>
                          <Form.Group as={Col} lg={4} sm={6}>
                            <Form.Label>Phone</Form.Label>
                            <Form.Control
                              as={MaskedInput}
                              name='providerPersonnelPhone'
                              mask='(111) 111-1111'
                              value={values.providerPersonnelPhone || ''}
                              disabled
                            />
                          </Form.Group>
                          <Form.Group as={Col} lg={2} sm={6}>
                            <Form.Label>Extension</Form.Label>
                            <Form.Control
                              type='text'
                              name='providerPersonnelExtension'
                              value={`${values.providerPersonnelExtension || ''}`}
                              disabled
                              maxLength={4}
                            />
                          </Form.Group>
                        </Row>
                        <Row>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Provider Personnel Email</Form.Label>
                            <Form.Control
                              type='text'
                              name='providerPersonnelEmailAddress'
                              value={values.providerPersonnelEmailAddress || ''}
                              disabled
                            />
                          </Form.Group>
                        </Row>
                        <Row>
                          <Form.Group as={Col} lg={6} sm={12}>
                            <Form.Label>Additional Email Recipient(s)</Form.Label>
                            <Form.Control
                              type='text'
                              name='additionalRecipients'
                              value={values.additionalRecipients || ''}
                              disabled
                            />
                          </Form.Group>
                        </Row>
                      </Card.Body>
                    </Card>
                    <CardEmployeeForm
                      errors={errors}
                      handleChange={handleChange}
                      index={1}
                      readonly={readonly}
                      selectedProvider={selectedProvider}
                      user={user}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      values={values}
                    />
                    <AccordionEmployeeForm
                      errors={errors}
                      handleChange={handleChange}
                      index={2}
                      readonly={readonly}
                      selectedProvider={selectedProvider}
                      user={user}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      values={values}
                    />
                    <AccordionEmployeeForm
                      errors={errors}
                      handleChange={handleChange}
                      index={3}
                      readonly={readonly}
                      selectedProvider={selectedProvider}
                      user={user}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      values={values}
                    />
                    <AccordionEmployeeForm
                      errors={errors}
                      handleChange={handleChange}
                      index={4}
                      readonly={readonly}
                      selectedProvider={selectedProvider}
                      user={user}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      values={values}
                    />
                    {
                      values.activity && values.activity.length > 0 && (
                        <Card className='background-check-form-card'>
                          <Card.Body className='background-check-form-card-body'>
                            <Card.Title>Approval Process Activity Log</Card.Title>
                            <Form.Group as={Col} xs={12} style={{ marginTop: 20 }}>
                              <div>
                                <ToolkitProvider
                                  keyField='id'
                                  bootstrap4
                                  data={ values.activity.concat(values.followUp).sort((a, b) => moment(b.date).diff(moment(a.date))).map(activity => ({...activity, id: `${activity.id}_${activity.activityType}`})) }
                                  columns={ columns }
                                  search
                                >
                                  {
                                    props => (
                                      <div style={{width: '100%'}}>
                                        <BootstrapTable
                                          striped
                                          condensed
                                          { ...props.baseProps }
                                        />
                                      </div>
                                    )
                                  }
                                </ToolkitProvider>
                              </div>
                            </Form.Group>
                          </Card.Body>
                        </Card>
                      )
                    }
                    <div className='background-check-btn-container'>
                      <Button
                        variant='secondary'
                        className='custom-button mr-2'
                        onClick={onClose}
                      >
                        Cancel
                      </Button>
                      {
                        !readonly && (
                          <Button
                            variant='info'
                            type='submit'
                          >
                            Submit
                          </Button>
                        )
                      }
                    </div>
                  </Form>
                )}
              </Formik>
            )
          }
        </Modal.Body>
      </Modal>
    )
  }

export default BackgroundCheckVerificationModal